import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { LayoutDashboard, Home, Kanban, Users2, Wallet, Target, Settings, Bell, LogOut, Sparkles, LayoutGrid, BarChart3, PieChart, BookOpen, Sun, Moon, Trophy, Trash2, MessagesSquare, UserCircle, ExternalLink, CheckCircle2, XCircle, ClipboardCheck, Calculator } from "lucide-react";
import { fmtDateTime } from "@/lib/format";
import { getStoredTheme, setTheme as applyThemePref } from "@/lib/theme";
import Tour from "@/components/Tour";
import EquityLogo from "@/components/EquityLogo";
import { useBranding } from "@/hooks/useBranding";
import ProfileDialog from "@/components/ProfileDialog";
import Avatar from "@/components/Avatar";
import { playNotifSound, requestNotifPermission, showBrowserNotification, vibrateDevice } from "@/lib/notifySound";
import { subscribeToPush, ensurePushSubscribed } from "@/lib/webPush";
import Sidebar, { getStoredSidebarCollapsed } from "@/components/Sidebar";
import useRealtimeSync from "@/hooks/useRealtimeSync";

const PRIMARY = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/pipeline", label: "Pipeline", icon: Kanban },
  { to: "/leads", label: "Leads", icon: Users2 },
];
const MOBILE_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: Home },
  { to: "/pipeline", label: "Pipeline", icon: Kanban },
  { to: "/insight", label: "Insight", icon: PieChart },
  { to: "/piutang", label: "Piutang", icon: Wallet },
];
const SECONDARY = [
  { to: "/piutang", label: "Piutang", icon: Wallet },
  { to: "/rekap", label: "Rekap Omset", icon: BarChart3 },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/insight", label: "Insight", icon: PieChart },
  { to: "/chat", label: "Chat Tim", icon: MessagesSquare },
  { to: "/target", label: "Target & Komisi", icon: Target },
  { to: "/kalkulator", label: "Kalkulator", icon: Calculator },
  { to: "/panduan", label: "Panduan", icon: BookOpen },
];

function NotifDropdown({ testidPrefix, notifs, notifPermission, enableNotifSound, openNotif, navigate }) {
  const qc = useQueryClient();
  const [busyRef, setBusyRef] = useState(null);
  // Controlled open state — the notification rows below are plain divs (not
  // DropdownMenuItem, which auto-closes on select), so without this the popup used
  // to just sit there after you clicked a notification, needing a second click to
  // dismiss. Every action here now explicitly closes it, like a normal app.
  const [open, setOpen] = useState(false);
  const goTo = (path) => { setOpen(false); navigate(path); };
  const approveFromNotif = async (e, refId) => {
    e.stopPropagation();
    setBusyRef(refId);
    try {
      await api.post(`/pending-changes/${refId}/approve`);
      toast.success("Perubahan disetujui & diterapkan");
      qc.invalidateQueries();
      setOpen(false);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusyRef(null); }
  };
  // window.prompt() is unavailable in some browser/PWA contexts (e.g. installed
  // standalone mode), so rejection — which wants a reason — goes to the full
  // Antrian Persetujuan page instead of a blocking native dialog here.
  const rejectFromNotif = (e) => {
    e.stopPropagation();
    goTo("/persetujuan");
  };
  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o) openNotif(); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid={`${testidPrefix}notif-bell`} className="relative w-9 h-9 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5">
          <Bell size={18} />
          {notifs.data?.unread > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#18181B] border-white/10 w-80 max-h-96 overflow-y-auto">
        <DropdownMenuLabel>Notifikasi</DropdownMenuLabel>
        {notifPermission !== "granted" && notifPermission !== "unsupported" && (
          <div className="mx-2 mb-2 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-xs text-blue-200 mb-1.5">
              {notifPermission === "denied"
                ? "Notifikasi suara/desktop diblokir browser. Aktifkan lewat setelan izin situs di browser kamu."
                : "Nyalakan notifikasi suara & desktop supaya tidak ketinggalan chat/mention."}
            </p>
            {notifPermission !== "denied" && (
              <button onClick={(e) => { e.preventDefault(); enableNotifSound(); }} className="text-xs font-medium px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white">
                Aktifkan Notifikasi
              </button>
            )}
          </div>
        )}
        <DropdownMenuSeparator className="bg-white/10" />
        {(notifs.data?.items || []).length === 0 && <div className="px-3 py-6 text-center text-sm text-zinc-500">Belum ada notifikasi</div>}
        {(notifs.data?.items || []).slice(0, 15).map((n) => {
          // "approval" (still pending) has its own Setujui/Tolak/Lihat Detail buttons below,
          // so the row itself stays inert there to avoid misclicks — but "approval_resolved"
          // has no buttons at all, so it must be clickable itself or tapping it does nothing.
          const clickable = n.kind !== "approval";
          return (
          <div key={n.notif_id}
            onClick={() => {
              if (!clickable) return;
              // Approval routing must win over the plain lead_id branch below — a
              // "Menunggu Persetujuan Dokumen" notification carries a lead_id (so the
              // bell text can name the client) *and* a chg_ ref_id, and used to fall
              // into the lead_id branch first, landing reviewers on the lead sheet
              // instead of the actual approval queue.
              if (n.kind === "approval_resolved" || n.ref_id?.startsWith("chg_")) return goTo("/persetujuan");
              // `tab` (set by the backend when it knows which tab is relevant — "diskusi"
              // for a chat/mention reply, "bayar" for a termin reminder, "aktivitas" for a
              // follow-up reminder) deep-links straight there instead of always landing on
              // the lead's default Info tab.
              if (n.lead_id) return goTo(`/leads?open=${n.lead_id}${n.tab ? `&tab=${n.tab}` : ""}`);
              // Same routing rule as the push-notification payload on the backend —
              // any chat/mention notification (not just @mentions) opens the actual
              // conversation, and straight into the right group when ref_id (room_id)
              // is set, instead of landing on the generic Chat Tim tab every time.
              if (n.kind === "chat" || n.kind === "mention") return goTo(n.ref_id ? `/chat?room=${n.ref_id}` : "/chat");
              if (n.kind === "target") return goTo("/target");
              goTo("/dashboard");
            }}
            className={`px-3 py-2.5 border-b border-white/5 last:border-0 hover:bg-white/5 ${clickable ? "cursor-pointer" : ""}`}>
            <div className="flex items-start gap-1.5">
              {n.kind === "approval_resolved" && n.resolution === "approved" && <CheckCircle2 size={14} className="text-green-500 mt-0.5 shrink-0" />}
              {n.kind === "approval_resolved" && n.resolution === "rejected" && <XCircle size={14} className="text-red-500 mt-0.5 shrink-0" />}
              <p className="text-sm font-medium">{n.title}</p>
            </div>
            <p className="text-xs text-zinc-400">{n.message}</p>
            <p className="text-[10px] text-zinc-600 mt-0.5">{fmtDateTime(n.created_at)}</p>
            {n.kind === "approval" && n.ref_id && (
              <div className="flex items-center gap-2 mt-2">
                <button onClick={(e) => approveFromNotif(e, n.ref_id)} disabled={busyRef === n.ref_id}
                  className="text-xs font-medium px-2.5 py-1 rounded-md bg-green-700 hover:bg-green-800 text-white disabled:opacity-50">Setujui</button>
                <button onClick={rejectFromNotif}
                  className="text-xs font-medium px-2.5 py-1 rounded-md bg-[#27272a] hover:bg-[#323236] text-red-400">Tolak</button>
                <button onClick={(e) => { e.stopPropagation(); goTo("/persetujuan"); }}
                  className="text-xs font-medium text-blue-400 hover:text-blue-300 underline underline-offset-2 flex items-center gap-1 ml-auto">
                  Lihat Detail Lengkap <ExternalLink size={11} />
                </button>
              </div>
            )}
          </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenuDropdown({ testidPrefix, user, setProfileOpen, setTourOpen, logout, navigate, theme, toggleTheme, showThemeToggle }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`${testidPrefix}user-menu`}>
          <Avatar user={user} size={36} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#18181B] border-white/10 w-52">
        <DropdownMenuLabel>
          <div className="text-sm">{user?.name}</div>
          <div className="text-xs text-zinc-500 font-normal capitalize">{user?.role}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem data-testid={`${testidPrefix}open-profile`} onClick={() => setProfileOpen(true)} className="focus:bg-white/5">
          <UserCircle size={15} className="mr-2" />Profil Saya
        </DropdownMenuItem>
        {showThemeToggle && (
          <DropdownMenuItem data-testid={`${testidPrefix}theme-toggle-menu`} onClick={(e) => { e.preventDefault(); toggleTheme(); }} className="focus:bg-white/5">
            {theme === "light" ? <Moon size={15} className="mr-2" /> : <Sun size={15} className="mr-2" />}
            {theme === "light" ? "Mode Gelap" : "Mode Terang"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid={`${testidPrefix}replay-tour`} onClick={() => setTourOpen(true)} className="focus:bg-white/5">
          <Sparkles size={15} className="mr-2" />Lihat Tur Fitur
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testidPrefix}logout-btn`} onClick={async () => { await logout(); navigate("/login"); }} className="text-red-400 focus:text-red-400 focus:bg-red-500/10">
          <LogOut size={15} className="mr-2" />Keluar
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  // One real-time connection for the whole authenticated app — a change made on any device
  // (desktop, another phone) shows up here within about a second, no manual refresh needed.
  useRealtimeSync();
  const { companyName } = useBranding();
  const [moreOpen, setMoreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [theme, setThemeState] = useState(getStoredTheme());
  const toggleTheme = () => { const next = theme === "light" ? "dark" : "light"; setThemeState(next); applyThemePref(next); };
  const [tourOpen, setTourOpen] = useState(false);
  React.useEffect(() => { if (!localStorage.getItem("crm_tour_done")) setTourOpen(true); }, []);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getStoredSidebarCollapsed);

  // Hide the floating bottom nav whenever a text field is focused (mobile keyboard open) —
  // otherwise on iOS the fixed-position nav floats awkwardly right above the keyboard,
  // overlapping the input (worst on chat screens). Applies app-wide, not just chat.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  React.useEffect(() => {
    const isTextEntry = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === "TEXTAREA") return true;
      if (tag === "INPUT") {
        const type = (el.getAttribute("type") || "text").toLowerCase();
        return !["checkbox", "radio", "range", "file", "button", "submit", "color"].includes(type);
      }
      return el.isContentEditable;
    };
    const onFocusIn = (e) => { if (isTextEntry(e.target)) setKeyboardOpen(true); };
    const onFocusOut = (e) => { if (isTextEntry(e.target)) setKeyboardOpen(false); };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => { document.removeEventListener("focusin", onFocusIn); document.removeEventListener("focusout", onFocusOut); };
  }, []);
  // Mobile header + bottom nav slide out of view while scrolling down (more room to read
  // the page) and slide back in as soon as the user scrolls up even slightly, or is near
  // the very top — the same "hide on scroll down, reveal on scroll up" pattern most
  // native/mobile-web apps use. A small threshold (5px) avoids twitchy flicker on the
  // sub-pixel scroll jitter some mobile browsers report.
  const [chromeHidden, setChromeHidden] = useState(false);
  React.useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 80) setChromeHidden(false);
      else if (y > lastY + 5) setChromeHidden(true);
      else if (y < lastY - 5) setChromeHidden(false);
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem("equity_sidebar_collapsed", next ? "1" : "0"); } catch { /* best-effort */ }
      return next;
    });
  };

  const secondary = [...SECONDARY];
  if (["admin", "owner", "chief", "manager"].includes(user?.role)) secondary.push({ to: "/persetujuan", label: "Persetujuan", icon: ClipboardCheck });
  if (["admin", "owner", "chief"].includes(user?.role)) secondary.push({ to: "/admin", label: "Pengaturan", icon: Settings });
  const mobileMenu = [{ to: "/pipeline", label: "Pipeline", icon: Kanban }, { to: "/leads", label: "Leads", icon: Users2 }, ...secondary];
  const sidebarGroups = [
    { label: "Menu Utama", items: [...PRIMARY, ...secondary.slice(0, 2)] },
    { label: "Analisis & Tim", items: secondary.slice(2, 6) },
    { label: "Lainnya", items: secondary.slice(6) },
  ].filter((g) => g.items.length > 0);

  const notifs = useQuery({ queryKey: ["notifications"], queryFn: async () => (await api.get("/notifications")).data, refetchInterval: 15000 });
  const openNotif = async () => { await api.post("/notifications/read"); qc.invalidateQueries({ queryKey: ["notifications"] }); };
  const lastNotifIdRef = React.useRef(null);
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const enableNotifSound = async () => {
    playNotifSound(user?.notif_prefs?.sound);
    if (typeof Notification === "undefined") return;
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm === "granted") { try { await subscribeToPush(); } catch { /* best-effort */ } }
  };
  // Re-validated on every foreground return, not just once at mount — a push subscription
  // can silently go stale (browser rotated it, the device was offline for a long stretch,
  // etc.), and the only way to catch that is to re-sync it with the server periodically
  // instead of assuming the one from first load is still good months later.
  React.useEffect(() => {
    ensurePushSubscribed();
    const onVisible = () => { if (document.visibilityState === "visible") ensurePushSubscribed(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
  React.useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (e) => { if (e.data?.type === "notif-navigate" && e.data.url) navigate(e.data.url); };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [navigate]);
  React.useEffect(() => {
    const items = notifs.data?.items || [];
    if (!items.length) return;
    const newestId = items[0].notif_id;
    if (lastNotifIdRef.current === null) { lastNotifIdRef.current = newestId; return; }
    if (newestId !== lastNotifIdRef.current) {
      const prefs = user?.notif_prefs;
      if (prefs?.enabled !== false) {
        playNotifSound(prefs?.sound);
        showBrowserNotification(items[0].title, items[0].message);
        if (prefs?.vibrate !== false) vibrateDevice();
      }
      lastNotifIdRef.current = newestId;
    }
  }, [notifs.data, user]);
  const isActive = (to) => location.pathname === to;
  const go = (to) => { setMoreOpen(false); navigate(to); };

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 md:flex">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        groups={sidebarGroups}
        isActive={isActive}
        onNavigate={navigate}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile header */}
        <header className={`glass sticky top-0 z-40 shadow-[0_1px_0_rgba(255,255,255,0.06)] px-4 h-[calc(3.5rem+env(safe-area-inset-top)+0.75rem)] flex items-end pb-2.5 justify-between md:hidden transition-transform duration-200 ${chromeHidden ? "-translate-y-full" : ""}`}>
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 sm:gap-2.5 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#020617] overflow-hidden flex items-center justify-center shrink-0"><EquityLogo size={36} /></div>
            <span className="flex flex-col items-start leading-none min-w-0">
              <span className="font-heading text-xs sm:text-sm font-medium tracking-[0.1em] sm:tracking-[0.14em] uppercase text-zinc-200 truncate">Equity<span className="text-blue-400">.CRM</span></span>
              <span className="text-[9px] sm:text-[10px] text-zinc-500 font-normal mt-0.5 sm:mt-1 truncate max-w-[120px] sm:max-w-none">{companyName}</span>
            </span>
          </button>

          <div className="flex items-center gap-1">
            <NotifDropdown testidPrefix="" notifs={notifs} notifPermission={notifPermission} enableNotifSound={enableNotifSound} openNotif={openNotif} navigate={navigate} />
            <UserMenuDropdown testidPrefix="" user={user} setProfileOpen={setProfileOpen} setTourOpen={setTourOpen} logout={logout} navigate={navigate} theme={theme} toggleTheme={toggleTheme} showThemeToggle />
          </div>
        </header>

        {/* Desktop top bar */}
        <header className="glass hidden md:flex sticky top-0 z-30 h-16 items-center justify-end gap-1.5 px-6 border-b border-white/10">
          <button data-testid="theme-toggle-desktop" onClick={toggleTheme} className="w-9 h-9 rounded-lg flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5" title="Ganti tema">
            {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          <NotifDropdown testidPrefix="desktop-" notifs={notifs} notifPermission={notifPermission} enableNotifSound={enableNotifSound} openNotif={openNotif} navigate={navigate} />
          <UserMenuDropdown testidPrefix="desktop-" user={user} setProfileOpen={setProfileOpen} setTourOpen={setTourOpen} logout={logout} navigate={navigate} />
        </header>

        <main className="flex-1 pb-[calc(6rem+env(safe-area-inset-bottom))] md:pb-8"><Outlet /></main>

      {/* Bottom nav (mobile) — floating tech style. Hidden while typing so it doesn't
          float above the keyboard and cover the input (worst on chat screens), and while
          scrolling down (more reading room), same as the header above. */}
      <nav className={`fixed left-3 right-3 z-50 md:hidden transition-transform duration-200 ${(keyboardOpen || chromeHidden) ? "translate-y-[calc(100%+2rem)] pointer-events-none" : ""}`} style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
        <div className="relative h-16 rounded-2xl glass border border-white/10 shadow-[0_10px_32px_rgba(0,0,0,0.5)] flex items-center justify-around px-1.5 overflow-hidden">
          <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/50 to-transparent" />
          {MOBILE_NAV.map((n) => { const Icon = n.icon; const active = isActive(n.to); return (
            <button key={n.to} data-testid={`bottomnav-${n.label.toLowerCase()}`} onClick={() => navigate(n.to)}
              className={`relative flex flex-col items-center justify-center gap-0.5 h-12 flex-1 rounded-xl transition-all duration-200 ${active ? "text-blue-300 bg-blue-500/15" : "text-zinc-500"}`}>
              <Icon size={19} /><span className="text-[10px] font-medium">{n.label}</span>
            </button>
          ); })}

          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button data-testid="bottomnav-more" className={`flex flex-col items-center justify-center gap-0.5 h-12 flex-1 rounded-xl transition-all duration-200 ${moreOpen ? "text-blue-300 bg-blue-500/15" : "text-zinc-500"}`}>
                <LayoutGrid size={19} /><span className="text-[10px] font-medium">Menu</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-[#0f0f12] border-white/10 rounded-t-3xl pb-[env(safe-area-inset-bottom)]">
              <SheetHeader className="pr-8"><SheetTitle className="font-heading text-left">Menu</SheetTitle></SheetHeader>
              <div className="grid grid-cols-3 gap-3 mt-5 pb-6">
                {mobileMenu.map((n) => { const Icon = n.icon; return (
                  <button key={n.to} data-testid={`menu-${n.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => go(n.to)}
                    className="menu-grid-card flex flex-col items-center gap-2 p-4 rounded-2xl card-surface hover:border-white/20 transition-colors">
                    <div className="menu-grid-icon w-10 h-10 rounded-xl bg-blue-500/15 text-blue-300 flex items-center justify-center"><Icon size={20} /></div>
                    <span className="text-xs font-medium text-center">{n.label}</span>
                  </button>
                ); })}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      </div>

      <Tour open={tourOpen} onClose={() => setTourOpen(false)} />
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}
